import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { adminLogin, adminMe, clearToken, formatApiError, getToken } from "@/lib/api";
import { FOUNDER_PAGES, safeFounderReturn } from "@/lib/access";
import { Seo } from "@/components/Seo";
import { useSweep } from "@/components/primitives";

const WORKFLOW_GROUP = {
  "/founder/cfo": "ledger", "/founder/treasury": "treasury", "/founder/close": "close",
  "/founder/planning": "planning", "/founder/assets": "assets", "/founder/procurement": "purchasing",
  "/founder/contract-to-cash": "revenue", "/founder/tax-compliance": "tax",
};

export function FounderLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedPage = new URLSearchParams(location.search).get("next");
  const destination = safeFounderReturn(requestedPage);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sweep = useSweep();

  useEffect(() => {
    let current = true;
    if (getToken()) adminMe().then((user) => {
      if (current) navigate(requestedPage ? destination : safeFounderReturn(user.preferences?.default_landing), { replace: true });
    }).catch(() => {});
    return () => { current = false; };
  }, [destination, requestedPage, navigate]);

  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      await adminLogin(email.trim(), password);
      setPassword("");
      const user = await adminMe();
      navigate(requestedPage ? destination : safeFounderReturn(user.preferences?.default_landing), { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "We couldn’t reach your workspace. Please try again."));
    } finally { setBusy(false); }
  }

  return (
    <main className="min-h-screen glow-navy flex items-center justify-center px-6 py-20" data-testid="founder-login">
      <Seo title="Founder Office" description="Private financial intelligence and portfolio oversight for Jennae Michelle Holdings." />
      <div className="w-full max-w-md">
        <Link to="/" className="font-display gold-text text-sm tracking-wide2">Jennae Michelle Holdings</Link>
        <h1 className="font-display text-3xl mt-10 mb-3">Founder Office</h1>
        <p className="text-[var(--muted)] text-sm leading-relaxed mb-8">Your financial overview, forecasts, records, and portfolio decisions in one workspace.</p>
        <form onSubmit={submit} className="space-y-5" data-testid="founder-login-form">
          <div>
            <label htmlFor="founder-email" className="block text-sm mb-2">Email</label>
            <input id="founder-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="founder-input" />
          </div>
          <div>
            <label htmlFor="founder-password" className="block text-sm mb-2">Password</label>
            <input id="founder-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="founder-input" />
          </div>
          {error && <p role="alert" className="text-sm text-[#e7a877]">{error}</p>}
          <button {...sweep} type="submit" disabled={busy} className="jmh-cta w-full disabled:opacity-50" data-testid="founder-login-submit">{busy ? "Signing in…" : "Enter Founder Office"}</button>
        </form>
        <Link to="/" className="inline-block mt-8 text-sm text-[var(--muted)] hover:text-[var(--bone)]">Return to Holdings</Link>
      </div>
    </main>
  );
}

export function FounderAccess() {
  const location = useLocation();
  const [state, setState] = useState("checking");
  const [retry, setRetry] = useState(0);
  const requestCheck = useCallback(() => setRetry((value) => value + 1), []);

  useEffect(() => {
    let current = true;
    if (!getToken()) { setState("signed-out"); return; }
    setState("checking");
    adminMe().then(() => { if (current) setState("ready"); }).catch((err) => {
      if (current) setState([401, 403].includes(err.response?.status) ? "signed-out" : "unavailable");
    });
    return () => { current = false; };
  }, [retry]);

  useEffect(() => {
    window.addEventListener("jmh-session-change", requestCheck);
    window.addEventListener("storage", requestCheck);
    return () => {
      window.removeEventListener("jmh-session-change", requestCheck);
      window.removeEventListener("storage", requestCheck);
    };
  }, [requestCheck]);

  if (state === "signed-out") return <Navigate to={`/login?next=${encodeURIComponent(safeFounderReturn(location.pathname))}`} replace />;
  if (state !== "ready") return (
    <main className="min-h-screen glow-navy flex items-center justify-center px-6 text-center">
      <div>
        <p role="status">{state === "checking" ? "Opening your workspace…" : "Your workspace is temporarily unavailable."}</p>
        {state === "unavailable" && <button type="button" onClick={requestCheck} className="jmh-cta mt-6">Try again</button>}
        <Link to="/" className="block mt-6 text-sm text-[var(--muted)]">Return to Holdings</Link>
      </div>
    </main>
  );
  return (
    <div className="founder-workspace">
      <header className="border-b border-[rgba(180,139,63,0.2)] px-4 py-4 md:px-8">
        <div className="flex justify-between items-center gap-4 mb-4">
          <div className="founder-brand"><Link to="/" className="font-display gold-text text-sm">Jennae Michelle Holdings</Link><span className="founder-brand-caption">Founder Office · Nirva Life ecosystem</span></div>
          <button type="button" onClick={clearToken} className="jmh-secondary">Sign out of JMH</button>
        </div>
        <nav aria-label="Founder workspace" className="flex flex-wrap gap-x-5 gap-y-3">
          {FOUNDER_PAGES.map(([to, label]) => <NavLink key={to} to={to} className={({isActive}) => `text-xs py-1 ${isActive ? "text-[var(--gold-lite)] underline underline-offset-4" : "text-[var(--muted)] hover:text-[var(--bone)]"}`}>{label}</NavLink>)}
        </nav>
      </header>
      {WORKFLOW_GROUP[location.pathname] && <div className="px-4 py-3 md:px-8 border-b border-[rgba(180,139,63,0.15)] text-sm">
        <Link className="text-[var(--gold-lite)] underline underline-offset-4" to={`/founder/operations?group=${WORKFLOW_GROUP[location.pathname]}`}>Manage the records behind this view →</Link>
      </div>}
      <Outlet />
    </div>
  );
}
